// @flow

import React from 'react';
import classNames from 'classnames';
import OptionGroup from './option_group';

const isGroup = option => option && Array.isArray(option.options);

const optionIsSelected = (
  values: Array<Object>,
  option: Object,
  // eslint-disable-next-line no-unused-vars
  removeSelected: boolean = false
) => {
  if (!values || values.length === 0) {
    //|| !removeSelected
    return false;
  }

  values = Array.isArray(values) ? values : [values];
  for (let i = 0; i < values.length; i++) {
    let value = values[i];
    if (value && value.label === option.label && value.value === option.value) {
      return true;
    }
  }

  return false;
};

const noop = () => {};

const buildOptionGroupRenderer = (menuRendererConfig: Object = {}) => (
  props: Object
) => {
  /*
  This function accepts an object of menuRendererConfig and returns a React
  component. The returned React component has props passed to it by React
  Select.
  This link is made in our `Select` component, where the return value of this
  function call is provided as the 'menuRenderer' prop to `Select`
  The code below (minus the menuRendererConfig wrapper) is heavily based on
  React Select Plus's default renderer.
  These props are the only props passed along by React Select.
  Any other necessary configuration should be passed in menuRendererConfig.
*/
  const {
    focusedOption,
    instancePrefix,
    onFocus,
    onOptionRef,
    onSelect,
    optionClassName,
    optionComponent,
    optionRenderer,
    options,
    valueArray,
    valueKey,
  } = props;

  let Option = optionComponent;

  const renderOptions = (optionsSubset: Array<Object>) => {
    const {
      selectedOptions,
      allowCategorySelection,
      noResultsText,
    } = menuRendererConfig;

    let selectableOptions = optionsSubset.map((option, i) => {
      if (isGroup(option)) {
        /*
          If group is selected, render nothing
          If group is not selected, render group title and its option children
        */
        const isSelected = optionIsSelected(
          selectedOptions,
          option,
          menuRendererConfig.removeSelected
        );

        if (
          allowCategorySelection &&
          isSelected &&
          menuRendererConfig.removeSelected
        ) {
          return null;
        }

        let optionGroupProps = {
          key: `option-group-${i}`,
          label: optionRenderer(option),
          option: option,
          optionIndex: i,
          onSelect: noop,
          isSelected: false,
          isDisabled: false,
        };

        /*
          Devs can enable the selection of categories by passing the
          allowCategorySelection prop to the Select wrapper.
          If this config is enabled and a category is selected, do not
          render its child options.
        */
        if (allowCategorySelection) {
          optionGroupProps.onSelect = onSelect;
          optionGroupProps.isSelected = isSelected;
          optionGroupProps.isDisabled = option.disabled;
        }

        return (
          <OptionGroup className="Select-option-group" {...optionGroupProps}>
            {renderOptions(option.options)}
          </OptionGroup>
        );
      } else {
        let isSelected;
        if (valueArray) {
          isSelected = optionIsSelected(
            valueArray,
            option,
            menuRendererConfig.removeSelected
          );
        } else if (menuRendererConfig.selectedOptions) {
          isSelected = optionIsSelected(
            menuRendererConfig.selectedOptions,
            option,
            menuRendererConfig.removeSelected
          );
        }

        if (isSelected && menuRendererConfig.removeSelected) {
          return null;
        }

        const isFocused = option === focusedOption;
        const optionClass = classNames(optionClassName, 'Select-option', {
          'is-selected': isSelected,
          'is-focused': isFocused,
          'is-disabled': option.disabled,
        });

        return (
          <Option
            className={optionClass}
            instancePrefix={instancePrefix}
            isDisabled={option.disabled}
            isFocused={isFocused}
            isSelected={isSelected}
            key={`option-${i}-${option[valueKey]}`}
            onFocus={onFocus}
            onSelect={onSelect}
            option={option}
            optionIndex={i}
            ref={ref => {
              onOptionRef(ref, isFocused);
            }}
          >
            {optionRenderer(option, i)}
          </Option>
        );
      }
    });
    // $FlowFixMe
    selectableOptions = selectableOptions.filter(option => !!option);
    if (!selectableOptions.length) {
      return <div className="Select-no-options">{noResultsText}</div>;
    }

    return selectableOptions;
  };

  return renderOptions(options);
};

export { buildOptionGroupRenderer };
